import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { backendUrl } from 'src/environments/environment';
import { Seller } from './authentication.store';
import { Chat } from './chat.store';
import { Product } from './product.store';
import { toPromise } from './toPromise';

export interface Orders {
  status: number;
  data: {
    orders: Array<OrdersData>;
    total: number;
    total_ongoing: number;
    total_cancel: number;
    total_return: number;
    total_pending: number;
    total_completed: number;
  };
}
export interface OrderDetails {
  status: number;
  data?: OrdersData;
  order_product: Array<Product>;
}
export interface OrdersData {
  id: number;
  order_number: number;
  user_id: number;
  seller_id: number;
  status: number;
  grand_total: number;
  discount: number;
  payment_status: string;
  payment_method: number;
  coupon_id: string;
  name: string;
  phone_number: number;
  delivery_name: string;
  delivery_phone_number: number;
  billing_locations: number;
  delivery_locations: string;
  notes: string;
  txn_no: number;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date;
  products: Array<Product>;
  order_product: Array<Product>;
  delivery_location: DeliveryLocation;
  billing_location: BillingLocation;
  seller: Seller;
  delivery_status: number;
  delivery_option: number;
  rating: Array<RatingPayload>;
  chat: Chat;
  delivery_charges: number;
  qrcode: string;
  receipt_link: string;
  returnorder: {
    status: boolean;
    description: string;
    reason: number;
    img: string;
    id: number;
  };
  cancelorder: {
    created_at: Date;
    deleted_at: Date;
    description: Date;
    id: number;
    order_id: number;
    reason: number;
    updated_at: Date;
  };
}
export interface DeliveryLocation {
  id: number;
  country: string;
  province: string;
  town: string;
  street: string;
  house_no: number;
  building: string;
  pincode: number;
  area: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date;
}
export interface BillingLocation {
  id: number;
  country: string;
  province: string;
  town: string;
  street: string;
  house_no: number;
  building: string;
  pincode: number;
  area: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date;
}

export enum OrderStatus {
  Pending,
  OnGoing,
  Completed,
  Cancel,
  Return,
  Confirmed,
  Blank,
}

export enum DeliveryOptions {
  Delivery,
  Pickup,
}
export interface ChangeStatus {
  status: OrderStatus;
  order_id: number;
}
export interface RatingPayload {
  order_id: number;
  star_rating: number;
  comments: string;
  img: String;
}

export interface RentalOrder {
  order: {
    id: number;
    items: Array<number>;
  };
}
@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  constructor(private httpClient: HttpClient) {}

  async getOrders(params): Promise<Orders> {
    const url = `${backendUrl}seller/order`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.get<Orders>(url, {
        params: params,
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }
  async changeStatus(data: ChangeStatus): Promise<Orders> {
    const url = `${backendUrl}seller/order-status`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<Orders>(url, data, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }

  async confirmRentalOrder(data: RentalOrder): Promise<Orders> {
    const url = `${backendUrl}seller/confirm-rental-order`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<Orders>(url, data, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }

  async assignRentalOrder(data: RentalOrder): Promise<Orders> {
    const url = `${backendUrl}seller/rental-order-assign-to-delivery-boy`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<Orders>(url, data, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }
  

  async changeCustomerStatus(data: ChangeStatus): Promise<Orders> {
    const url = `${backendUrl}order-status`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<Orders>(url, data, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }
  async changeDeliveryStatus(data: ChangeStatus): Promise<Orders> {
    const url = `${backendUrl}seller/delivery-status`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<Orders>(url, data, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }

  async getOrderDetails(confirmOrderId: number): Promise<OrderDetails> {
    const url = `${backendUrl}seller/order/${confirmOrderId}`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.get<OrderDetails>(url, {
        params: {},
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }

  async getCustomerOrder(params): Promise<Orders> {
    const url = `${backendUrl}order`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.get<Orders>(url, {
        params: params,
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }

  async getCustomerOrderDetails(confirmOrderId: number): Promise<OrderDetails> {
    const url = `${backendUrl}order/${confirmOrderId}`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.get<OrderDetails>(url, {
        params: {},
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }

  async cancelOrder(type: number = 0, body: {} = null): Promise<OrderDetails> {
    const url =
      type == 0
        ? `${backendUrl}cancelorder`
        : `${backendUrl}seller/cancelorder`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<OrderDetails>(url, body, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }
  async rateOrder(type: number = 0, body: RatingPayload = null): Promise<void> {
    const url =
      type == 0 ? `${backendUrl}rating` : `${backendUrl}seller/rating`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<void>(url, body, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }
  async returnOrder(body: {} = null): Promise<void> {
    const url = `${backendUrl}returnorder`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<void>(url, body, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }
  async returnOrderAcceptReject(body: {} = null): Promise<void> {
    const url = `${backendUrl}seller/return-status`;
    const token = localStorage.getItem('authToken');
    return toPromise(
      this.httpClient.post<void>(url, body, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
    );
  }
}
